import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Form from 'react-bootstrap/Form';
import { useFormik } from 'formik';
import { featureDetails } from 'helpers/schemas/provisionSchemas/initialValues';
import { featureDetailSchemas } from 'helpers/schemas/provisionSchemas/schemas';
import SectionHeader from 'helpers/ui-controls/ui-components/SectionHeader';
import ToggleDetailed from 'helpers/ui-controls/toggleDetailed';
import { mapServerValuesToInitialValues } from 'helpers/generic.methods';
import { toast } from 'react-toastify';
import {
  UpdateProvision,
  updateProvisionToState,
  getOfferedFeaturesByProvisioningId
} from 'features/provisioning/ProvisioningSlice';
import { useDispatch } from 'react-redux';
import {
  UilCommentAltMessage,
  UilEnvelopes,
  UilFacebookF,
  UilEnglishToChinese,
  UilFileCheckAlt,
  UilCalendarAlt,
  UilBars
} from '@iconscout/react-unicons';
// import SMS from '@iconscout/react-unicons/icons/uil-comment-alt-m';
import { success } from 'helpers/variables';
import { Enums, getObjectKeyByValue } from 'helpers/dropdown-enums';
const FeatureDetails = ({ formData }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [editable, setEditable] = useState(false);
  const [formValues, setFormValues] = useState(featureDetails);
  const [loading, setLoading] = useState(false);
  const [id, setProvisionId] = useState('');
  const [offeredFeatures, setOfferedFeatures] = useState([]);
  const offeredFeaturesEditableStatus = [
    { value: 1, name: 'Adding Information' },
    { value: 2, name: 'Waiting For Setup' },
    { value: 3, name: 'Need More Information' }
  ];
  const ProvisionFormHandler = async data => {
    try {
      setLoading(true);
      let payload = {
        ...{ features: offeredFeatures.filter(val => val.isSelected) },
        id: id
      };
      const response = await UpdateProvision(payload);
      dispatch(updateProvisionToState(response?.data?.data));
      toast.success(success.saveSuccess('Onboarding'));
      setEditable(false);
      setLoading(false);
    } catch (error) {
      toast.error(error || error.message);
      setLoading(false);
    }
  };

  const FeaturesFormIk = useFormik({
    enableReinitialize: true,
    initialValues: formValues,
    validationSchema: featureDetailSchemas,
    onSubmit: ProvisionFormHandler
  });

  useEffect(() => {
    setProvisionId(formData.id);
    mapServerValuesToInitialValues(formData, featureDetails)
      .then(data => {
        if (checkIfStatus_Editable(formData.status)) {
          getFeatures();
        }
        setFormValues({ ...featureDetails, ...data });
      })
      .catch(err => {
        throw new Error(err);
      });
  }, [formData]);

  const handleOnChangeFeature = (element, key, value) => {
    const temp = [...offeredFeatures];
    let index = key.split('-')[1];
    temp[index].isSelected = value;
    setOfferedFeatures(temp);
  };

  const getFeatures = () => {
    getOfferedFeaturesByProvisioningId({
      params: { id: formData.id }
    }).then(response => {
      if (response.data.success) {
        const { data } = response.data;
        let temp = data.map(item => ({
          ...item,
          isSelected: checkSelected(item)
        }));
        setOfferedFeatures(temp);
      }
    });
  };

  const checkSelected = obj => {
    let selectedFeatures = formData.features;
    return selectedFeatures.some(
      val =>
        val.id == obj.id &&
        val.description == obj.description &&
        val.paymentTypeId == obj.paymentTypeId
    );
  };

  const getPaymentType = paymentTypeId => {
    const paymentType = Enums.PaymentTypes.find(
      type => type.id == paymentTypeId
    );
    return paymentType.name;
  };

  const getFeatureIcon = id => {
    let featureIcons = {
      1: <UilCommentAltMessage className="mr-2 text-primary" />,
      2: <UilEnvelopes className="mr-2 text-primary" />,
      3: <UilFileCheckAlt className="mr-2 text-primary" />,
      4: <UilBars className="mr-2 text-primary" />,
      5: <UilCalendarAlt className="mr-2 text-primary" />,
      6: <UilEnglishToChinese className="mr-2 text-primary" />,
      7: <UilFacebookF className="mr-2 text-primary" />
    };
    return featureIcons[id];
  };

  const checkIfStatus_Editable = _status => {
    let isStatusEditable = offeredFeaturesEditableStatus.find(
      status => status.value == _status
    );
    if (isStatusEditable) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <Form
      className="form"
      onSubmit={FeaturesFormIk.handleSubmit}
      autoComplete="off"
    >
      <SectionHeader
        sectionHeading={t('company.provisioningDetails.heading6')}
        loading={loading}
        editable={editable}
        hardTurnOff={!checkIfStatus_Editable(formData.status)}
        handleEditableState={setEditable}
      />

      <section>
        <div className="section-body">
          <ul className="editable-fields-lists">
            {checkIfStatus_Editable(formData?.status)
              ? offeredFeatures.length > 0
                ? offeredFeatures.map((feature, index) => {
                    const { description, paymentTypeId, amount } = feature;
                    return (
                      <li
                        className={`list-group-item border-0 bg-lightgray-2 rounded mb-1 p-2`}
                        key={index}
                      >
                        <ToggleDetailed
                          heading={description}
                          flag={feature.isSelected}
                          listIcon={getFeatureIcon(feature.id)}
                          badges={[
                            `$${amount}`,
                            getObjectKeyByValue(
                              Enums.PaymentTypes,
                              paymentTypeId
                            )
                          ]}
                          titleText={
                            t('company.provisioningDetails.featureInfo', {
                              returnObjects: true,
                              description: description,
                              amount: `$${amount}`,
                              paymentType: getObjectKeyByValue(
                                Enums.PaymentTypes,
                                paymentTypeId
                              )
                            }).info
                          }
                          editable={editable}
                          handleChange={handleOnChangeFeature}
                          field={`feature-${index}`}
                        />
                      </li>
                    );
                  })
                : 'No features available'
              : formData?.features && formData?.features.length > 0
              ? formData.features.map((feature, index) => {
                  const { description, paymentTypeId, amount } = feature;
                  return (
                    <li
                      className={`list-group-item border-0 bg-lightgray-2 rounded mb-1 p-2`}
                      key={index}
                    >
                      <ToggleDetailed
                        heading={description}
                        flag={feature.isSelected}
                        listIcon={getFeatureIcon(feature.id)}
                        badges={[
                          `$${amount}`,
                          getObjectKeyByValue(Enums.PaymentTypes, paymentTypeId)
                        ]}
                        titleText={
                          t('company.provisioningDetails.featureInfo', {
                            returnObjects: true,
                            description: description,
                            amount: `$${amount}`,
                            paymentType: getObjectKeyByValue(
                              Enums.PaymentTypes,
                              paymentTypeId
                            )
                          }).info
                        }
                        editable={false}
                        field={feature.featureId}
                      />
                    </li>
                  );
                })
              : 'No features available'}
          </ul>
        </div>
      </section>
    </Form>
  );
};

export default FeatureDetails;
